<script setup lang="ts">
import { Link, usePage } from '@inertiajs/vue3';
import { Menu, X } from 'lucide-vue-next';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import LanguageSwitcher from '@/components/shared/LanguageSwitcher.vue';
import { useCurrentUrl } from '@/composables/useCurrentUrl';
import {
    dashboard,
    home,
    login,
    register,
    pricing,
    workflow,
    guide,
} from '@/routes';
import type { NavItem } from '@/types';

type Props = {
    canRegister?: boolean;
};

const props = defineProps<Props>();
const page = usePage();
const auth = computed(() => page.props.auth);
const { whenCurrentUrl } = useCurrentUrl();

const scrolled = ref(false);
const mobileOpen = ref(false);

const onScroll = () => {
    scrolled.value = window.scrollY > 20;
};

onMounted(() => {
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
});

onUnmounted(() => {
    window.removeEventListener('scroll', onScroll);
});

const logoHref = computed(() =>
    auth.value.user ? dashboard.url() : home.url(),
);

const mainNavItems: NavItem[] = [
    { title: 'Fonctionnalités', href: '/#features' },
    { title: 'Workflow', href: workflow.url() },
    { title: 'Tarifs', href: pricing.url() },
    { title: 'Guide', href: guide.url() },
    { title: 'FAQ', href: '/#faq' },
];
</script>

<template>
    <div class="fixed top-0 left-0 z-50 w-full px-4 pt-4">
        <header
            class="mx-auto max-w-5xl transition-all duration-500"
            :class="[
                scrolled
                    ? 'rounded-2xl border border-border/60 bg-background/80 shadow-lg shadow-black/[0.03] backdrop-blur-xl'
                    : 'rounded-2xl bg-transparent',
            ]"
        >
            <div class="flex h-14 items-center px-5">
                <!-- Mobile menu button -->
                <button
                    class="mr-3 flex items-center justify-center rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground lg:hidden"
                    @click="mobileOpen = !mobileOpen"
                >
                    <X v-if="mobileOpen" class="size-5" />
                    <Menu v-else class="size-5" />
                </button>

                <!-- Logo -->
                <Link
                    :href="logoHref"
                    class="flex items-center gap-2.5 transition-opacity hover:opacity-80"
                >
                    <div
                        class="flex size-7 shrink-0 items-center justify-center rounded-lg bg-brand text-[10px] font-bold tracking-tight text-white"
                    >
                        R
                    </div>
                    <span
                        class="hidden text-sm font-semibold tracking-tight text-foreground sm:inline-block"
                    >
                        Rewio
                    </span>
                </Link>

                <!-- Desktop nav — centered -->
                <nav class="hidden flex-1 justify-center lg:flex">
                    <ul class="flex items-center gap-1">
                        <li v-for="item in mainNavItems" :key="item.title">
                            <Link
                                v-if="!(item.href as string).startsWith('#')"
                                :href="item.href as string"
                                class="rounded-lg px-3 py-1.5 text-[13px] font-medium tracking-wide transition-colors"
                                :class="
                                    whenCurrentUrl(
                                        item.href,
                                        'text-foreground',
                                        'text-muted-foreground hover:bg-accent/60 hover:text-foreground',
                                    )
                                "
                            >
                                {{ item.title }}
                            </Link>
                            <a
                                v-else
                                :href="item.href as string"
                                class="rounded-lg px-3 py-1.5 text-[13px] font-medium tracking-wide text-muted-foreground transition-colors hover:bg-accent/60 hover:text-foreground"
                            >
                                {{ item.title }}
                            </a>
                        </li>
                    </ul>
                </nav>

                <!-- Right side -->
                <div class="ml-auto flex items-center gap-3">
                    <LanguageSwitcher />

                    <div class="hidden items-center gap-2 lg:flex">
                        <template v-if="auth.user">
                            <Link
                                :href="dashboard()"
                                class="rounded-lg px-3 py-1.5 text-[13px] font-medium text-muted-foreground transition-colors hover:text-foreground"
                            >
                                Mon espace
                            </Link>
                        </template>
                        <template v-else>
                            <Link
                                :href="login()"
                                class="rounded-lg px-3 py-1.5 text-[13px] font-medium text-muted-foreground transition-colors hover:text-foreground"
                            >
                                Connexion
                            </Link>
                            <Link
                                v-if="props.canRegister"
                                :href="register()"
                                class="rounded-full bg-brand px-4 py-1.5 text-[13px] font-medium text-brand-foreground transition-all hover:bg-brand/90"
                            >
                                Commencer
                            </Link>
                        </template>
                    </div>
                </div>
            </div>
        </header>

        <!-- Mobile nav panel -->
        <Transition
            enter-active-class="transition-all duration-300 ease-out"
            enter-from-class="opacity-0 -translate-y-2"
            enter-to-class="opacity-100 translate-y-0"
            leave-active-class="transition-all duration-200 ease-in"
            leave-from-class="opacity-100 translate-y-0"
            leave-to-class="opacity-0 -translate-y-2"
        >
            <div
                v-if="mobileOpen"
                class="mx-auto mt-2 max-w-5xl rounded-2xl border border-border/60 bg-background/95 p-5 shadow-lg backdrop-blur-xl lg:hidden"
            >
                <nav class="flex flex-col gap-1">
                    <template v-for="item in mainNavItems" :key="item.title">
                        <Link
                            v-if="!(item.href as string).startsWith('#')"
                            :href="item.href as string"
                            class="rounded-lg px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                            @click="mobileOpen = false"
                        >
                            {{ item.title }}
                        </Link>
                        <a
                            v-else
                            :href="item.href as string"
                            class="rounded-lg px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                            @click="mobileOpen = false"
                        >
                            {{ item.title }}
                        </a>
                    </template>
                </nav>

                <div class="mt-4 border-t border-border/50 pt-4">
                    <div class="flex flex-col gap-2">
                        <template v-if="auth.user">
                            <Link
                                :href="dashboard()"
                                class="rounded-xl bg-accent px-4 py-2.5 text-center text-sm font-medium text-foreground transition-colors hover:bg-accent/80"
                            >
                                Mon espace
                            </Link>
                        </template>
                        <template v-else>
                            <Link
                                :href="login()"
                                class="rounded-xl bg-accent px-4 py-2.5 text-center text-sm font-medium text-foreground transition-colors hover:bg-accent/80"
                            >
                                Connexion
                            </Link>
                            <Link
                                v-if="props.canRegister"
                                :href="register()"
                                class="rounded-xl bg-brand px-4 py-2.5 text-center text-sm font-medium text-brand-foreground transition-all hover:bg-brand/90"
                            >
                                Commencer gratuitement
                            </Link>
                        </template>
                    </div>
                </div>
            </div>
        </Transition>
    </div>

    <!-- Spacer to compensate for fixed navbar -->
    <div class="h-[calc(3.5rem+2rem)]"></div>
</template>
